import Button from "@components/global/new/Button/Button";
import EquipmentSelector from "@components/global/new/EquipmentSelector/EquipmentSelector";
import Field from "@components/global/new/Field/Field";
import SmallLink from "@components/global/new/SmallLink/SmallLink";
import Title from "@components/global/new/Title/Title";
import WithModal, { CommonModalPropsI } from "@components/HOC/withModals";
import { ConfirmModalExposedPropsI } from "@components/modals/newModals/ConfirmModal/ConfirmModal";
import { MODALS_ENUM } from "@constants/Modals";
import connectionsStore, { PlaceID3 } from "@mobx/store/connections";
import {
  EQUIPMENT_TYPE,
  IAvailableEquipment,
  IWorkEquipment,
} from "@store/Equipments/actionTypes";
import { getRandomColor } from "@utils/getRandomColor";
import { useGlobalNotifs } from "@utils/hooks/useGlobalNotifs";
import { useToasts } from "@utils/hooks/useToasts";
import { makeNotifs } from "@utils/makeNotifs";
import { ColorPicker } from "antd";
import cn from "classnames";
import { useMemo, useState } from "react";
import { useIntl } from "react-intl";
import { useDispatch } from "react-redux";
import { show } from "redux-modal";
import styles from "./EquipmentModal.module.scss";

export interface IEquipmentModal3Props extends CommonModalPropsI {
  readonly workEq?: IWorkEquipment;
  readonly templateEq?: IAvailableEquipment;
  readonly placeID?: PlaceID3;
  readonly showTypeSelector?: boolean;
}

interface Elems extends HTMLFormControlsCollection {
  name: HTMLInputElement;
  notes: HTMLTextAreaElement;
}

interface MyForm extends HTMLFormElement {
  readonly elements: Elems;
}

interface IEquipmentModal3Control {
  readonly color: string;
  readonly loading: boolean;
  readonly canSubmit: boolean;
  readonly equipment?: IAvailableEquipment | IWorkEquipment;
  readonly options: EQUIPMENT_TYPE[];

  onSubmit(e: React.FormEvent<MyForm>): void;
  onDeleteHandler(): void;
  onChangeColor(color: string): void;
}

const useControl = (
  props: IEquipmentModal3Props
): IEquipmentModal3Control => {
  const dispatch = useDispatch();
  const { onSuccess } = useGlobalNotifs();
  const { error } = useToasts();
  const { errors, ok } = makeNotifs();
  const equipment = useMemo(
    () => props.templateEq ?? props.workEq,
    [props.templateEq, props.workEq]
  );
  const placeStore = useMemo(
    () => (props.placeID ? connectionsStore.getPlaceStore(props.placeID) : undefined),
    [props.placeID]
  );
  const [color, setColor] = useState<string>(
    equipment?.color || getRandomColor()
  );
  const canSubmit = !!equipment && !!placeStore;
  const options = useMemo(
    () => (equipment ? [equipment.type] : Object.keys(EQUIPMENT_TYPE)),
    [equipment]
  ) as EQUIPMENT_TYPE[];

  const onSubmit = (e: React.FormEvent<MyForm>) => {
    e.preventDefault();

    const name = e.currentTarget.elements.name.value.trim();
    const notes = e.currentTarget.elements.notes.value;

    if (!name) return error(errors.fill);
    if (!equipment || !placeStore) return;

    placeStore.editNodeInfo(equipment.id, name, notes, color, equipment.type);
    onSuccess(ok.success);
    props.handleClose();
  };

  const onDeleteHandler = () => {
    if (!equipment || !placeStore) return;

    props.handleDestroy();

    const modalProps: ConfirmModalExposedPropsI = {
      title: props.formatMessage({
        id: "location.rack.rack_item.back.equipment.sure_delete",
      }),
      async onConfirm() {
        placeStore.removeNode(equipment.id);
        onSuccess(ok.success);
      },
    };

    dispatch(show(MODALS_ENUM.CONFIRM_MODAL, modalProps));
  };

  return {
    color,
    loading: false,
    canSubmit,
    equipment,
    options,

    onSubmit,
    onDeleteHandler,
    onChangeColor: setColor,
  };
};

function EquipmentModal3({ ...props }: IEquipmentModal3Props) {
  const { formatMessage } = useIntl();
  const control = useControl(props);
  const showTypeSelector = !!props.showTypeSelector;

  return (
    <div className={styles.wrapper}>
      <Title>
        {formatMessage({
          id: control.equipment ? "modal.equipment.edit" : "modal.equipment.create",
        })}
      </Title>

      <form className={styles.content} onSubmit={control.onSubmit}>
        <div
          className={cn(styles.fields, {
            [styles.fieldsSingleColumn]: !showTypeSelector,
          })}
        >
          <Field
            name={formatMessage({ id: "modal.equipment.edit.name" })}
            type='text'
            id='name'
            defaultValue={control.equipment?.name}
            className={styles.field}
          />
          {showTypeSelector && (
            <EquipmentSelector
              name={formatMessage({ id: "modal.equipment.choose" })}
              options={control.options}
              className={styles.selector}
              id='type'
            />
          )}
          <textarea
            id='notes'
            defaultValue={control.equipment?.info}
            className={styles.field_area}
            placeholder={formatMessage({
              id: "modal.equipment.notes.placeholder",
            })}
          />
          <div className={styles.color_wrapper}>
            <label>
              {formatMessage({ id: "modal.equipment.notes.color" })}:
            </label>
            <ColorPicker
              onChange={(_, hex) => control.onChangeColor(hex)}
              placement='top'
              defaultValue={control.color}
            >
              <div
                className={styles.color}
                style={{ backgroundColor: control.color }}
              />
            </ColorPicker>
          </div>
        </div>
        {!!props.workEq && (
          <SmallLink
            className={styles.delete}
            onClick={control.onDeleteHandler}
          >
            {formatMessage({
              id: "location.__delete",
            })}
          </SmallLink>
        )}
        <Button
          type='submit'
          className={styles.btn}
          loading={control.loading}
          disabled={!control.canSubmit}
        >
          {formatMessage({ id: "modal.equipment.btn_save" })}
        </Button>
      </form>
    </div>
  );
}

export default WithModal(MODALS_ENUM.EQUIPMENT_MODAL, EquipmentModal3);
